import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import { ShareMenu } from '../techcard/ShareMenu';
import { useTechCardStyles } from '../techcard/styles';
import { trackClick } from '../../utils/analytics';
import { type WnChange, type WnChangeTech, fmtDay } from './types';

/**
 * One "Technology changes at existing universities" card, opened inside What's
 * New (tracker, 2026-10-29):
 *   IP added   -> the new tech offers, each with Save (the bookmark Cosmos and
 *                 For You use), Share (the Cosmos share menu: copy link,
 *                 WhatsApp, LinkedIn) and a click that opens the tech card
 *   IP removed -> the titles of the removed tech offers
 * Data: GET /api/v1/whatsnew/change?id= (Backend whatsnew.change_items).
 */

interface Props {
  change: WnChange;
  onClose: () => void;
  onOpenTech?: (id: string) => void;
  /** "View the university on Cosmos" at the foot of an added list. */
  onOpenCosmos?: (code: string, name: string) => void;
}

const SAVED_EVENT = 'utech:saved-changed';   // ForYouPage listens for it

const Bookmark: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h12v18l-6-4-6 4z" /></svg>
);

export const ChangeTechs: React.FC<Props> = ({ change, onClose, onOpenTech, onOpenCosmos }) => {
  useTechCardStyles();
  const added = change.kind === 'added';
  const [items, setItems] = useState<WnChangeTech[] | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const showToast = (m: string) => {
    setToast(m);
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    let live = true;
    const uid = apiService.getStoredUserId();
    if (!uid) { setError('Please sign in again.'); return; }
    apiService.getWhatsNewChange(uid, change.id).then(r => {
      if (!live) return;
      if (r.success && r.data?.success) setItems(r.data.items || []);
      else setError((r.data as any)?.detail || r.error || 'Could not load these tech offers.');
    });
    return () => { live = false; };
  }, [change.id]);
  useEffect(() => {
    // Escape closes an open share menu first, the list on the next press.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.tcx-menu')) closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (toastT.current) clearTimeout(toastT.current); };
  }, []);

  const toggleSave = async (t: WnChangeTech) => {
    const uid = apiService.getStoredUserId();
    if (!uid) return;
    const on = !t.saved;
    setItems(xs => (xs || []).map(x => (x.id === t.id ? { ...x, saved: on } : x)));
    let ok = false;
    try {
      const res = await apiService.saveCard({ asset_type: 'technology', asset_id: t.id, bookmarked: on, liked: null,
                                              linked_chat_id: null }, uid);
      ok = !!res.success;
    } catch { ok = false; }
    if (!ok) {
      setItems(xs => (xs || []).map(x => (x.id === t.id ? { ...x, saved: !on } : x)));
      showToast('Could not update your bookmark. Please try again.');
      return;
    }
    trackClick('whatsnew_tech_save', { tech_id: t.id, on, code: change.code });
    window.dispatchEvent(new Event(SAVED_EVENT));
    showToast(on ? 'Saved to your technologies' : 'Removed from saved');
  };

  const open = (t: WnChangeTech) => {
    if (!t.available || !onOpenTech) return;
    trackClick('whatsnew_tech_open', { tech_id: t.id, code: change.code });
    onOpenTech(t.id);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`${added ? 'New' : 'Removed'} tech offers at ${change.name}`}
           className="w-full max-w-[720px] rounded-2xl bg-white shadow-xl flex flex-col max-h-[88vh]"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-3 border-b border-primary-200">
          <div className="flex flex-col gap-1 min-w-0">
            <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${added ? 'text-primary-700' : 'text-red-700'}`}>
              {added ? 'New tech offers' : 'Removed tech offers'} · {fmtDay(change.date, true)}
            </span>
            <h2 className="m-0 text-xl font-semibold text-grayscale-1300 break-words">{change.name}</h2>
            <span className="text-sm text-grayscale-1000">
              {added ? '+' : '−'}{change.n.toLocaleString()} listing{change.n === 1 ? '' : 's'}
              {items && items.length < change.n ? ` · showing ${items.length.toLocaleString()}` : ''}
            </span>
          </div>
          <button type="button" aria-label="Close" onClick={onClose}
                  className="shrink-0 w-9 h-9 rounded-full hover:bg-primary-100 text-grayscale-1100 text-xl leading-none">×</button>
        </div>

        <div className="overflow-y-auto px-5 sm:px-6 py-4 flex flex-col gap-2">
          {!items && !error && [0, 1, 2, 3].map(i => <div key={i} className="h-16 rounded-xl bg-primary-100/60 animate-pulse" />)}
          {error && <div className="text-sm text-red-700">{error}</div>}
          {items && items.length === 0 && (
            <div className="text-sm text-grayscale-1000">The list of these tech offers was not kept for this change.</div>
          )}
          {items && items.map(t => (added ? (
            <div key={t.id}
                 className={`flex items-start gap-3 rounded-xl border border-primary-200 px-4 py-3 ${t.available ? 'hover:bg-primary-100/40 cursor-pointer' : 'opacity-60'}`}
                 onClick={() => open(t)}>
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <span className="text-[15px] font-semibold text-grayscale-1300 break-words">{t.title}</span>
                <span className="text-[13px] text-grayscale-1000">
                  {t.available ? [t.university, t.area].filter(Boolean).join(' · ') || 'University technology'
                    : 'No longer listed on Cosmos'}
                </span>
                {t.available && t.tags.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {t.tags.slice(0, 2).map(g => (
                      <span key={g} className="text-xs px-2.5 py-0.5 rounded-full bg-primary-100 text-grayscale-1200">{g}</span>
                    ))}
                  </div>
                )}
              </div>
              {t.available && (
                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                  <button type="button" aria-pressed={!!t.saved} title={t.saved ? 'Remove from saved' : 'Save'}
                          aria-label={t.saved ? `Remove ${t.title} from saved` : `Save ${t.title}`}
                          onClick={() => void toggleSave(t)}
                          className="w-9 h-9 rounded-full flex items-center justify-center text-primary-700 hover:bg-primary-100">
                    <Bookmark filled={!!t.saved} />
                  </button>
                  <div className="tcx">
                    <ShareMenu techId={t.id} title={t.title} surface="whatsnew" onToast={showToast}
                               label="Share this tech offer" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div key={t.id} className="rounded-xl border border-primary-200 px-4 py-3 text-[15px] text-grayscale-1300 break-words">
              {t.title}
            </div>
          )))}
        </div>

        {added && onOpenCosmos && (
          <div className="px-5 sm:px-6 py-3 border-t border-primary-200">
            <button type="button" onClick={() => { onClose(); onOpenCosmos(change.code, change.name); }}
                    className="text-sm font-medium text-primary-700 hover:text-primary-600 hover:underline underline-offset-2">
              See all tech offers from {change.name} on Cosmos →
            </button>
          </div>
        )}
        {toast && (
          <div role="status" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full bg-grayscale-1300 text-white text-sm px-4 py-2 shadow-lg">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeTechs;
